if (!customElements.get('wm-mobile-app-nav')) {
  class WmMobileAppNav extends HTMLElement {
    connectedCallback() {
      this.media = window.matchMedia('(max-width: 749.98px)');
      this.lifecycle = new AbortController();
      this.revision = 0;
      this.links = Object.fromEntries([...this.querySelectorAll('[data-wm-nav-action]')].map(link => [link.dataset.wmNavAction, link]));
      this.badge = this.querySelector('[data-wm-cart-count]');
      this.media.addEventListener('change', () => this.syncViewport(), { signal: this.lifecycle.signal });
      this.syncViewport();
    }

    disconnectedCallback() {
      this.lifecycle?.abort();
      this.stopMobile();
    }

    syncViewport() {
      if (!this.media.matches) return this.stopMobile();
      if (this.mobile) return;
      this.mobile = new AbortController();
      const options = { signal: this.mobile.signal };
      this.addEventListener('click', event => this.activate(event), options);
      this.addEventListener('keydown', event => {
        if (event.key === ' ' && event.target.matches('[data-wm-nav-action][role="button"]')) {
          event.preventDefault();
          event.target.click();
        }
      }, options);
      for (const name of ['cart-add', 'cart-update', 'cart-remove']) {
        document.addEventListener(name, event => this.cartChanged(event.detail), options);
      }
      document.addEventListener('cart-drawer-open', () => this.links.cart.setAttribute('aria-expanded', 'true'), options);
      document.addEventListener('cart-drawer-closed', () => this.links.cart.setAttribute('aria-expanded', 'false'), options);
      document.addEventListener('shopify:section:load', () => this.bindNativeControls(), options);
      window.addEventListener('pageshow', event => { if (event.persisted) this.queueRefresh(); }, options);
      this.bindNativeControls();
      for (const name of ['header-drawer', 'cart-drawer', 'details-modal']) {
        customElements.whenDefined(name).then(() => {
          if (this.isConnected && this.mobile) this.bindNativeControls();
        });
      }
    }

    stopMobile() {
      this.mobile?.abort();
      this.mobile = null;
      this.nativeBindings?.abort();
      this.badgeObserver?.disconnect();
      this.refreshRequest?.abort();
      window.clearTimeout(this.refreshTimer);
      if (this.headerFrame) cancelAnimationFrame(this.headerFrame);
      document.documentElement.classList.remove('wm-mobile-nav-reveal-header');
    }

    bindNativeControls() {
      this.nativeBindings?.abort();
      this.nativeBindings = new AbortController();
      const options = { signal: this.nativeBindings.signal };
      this.menuDetails = document.getElementById('Details-menu-drawer-container');
      if (typeof this.menuDetails?.closest('header-drawer')?.openMenuDrawer === 'function') {
        this.links.categories.setAttribute('role', 'button');
        this.links.categories.setAttribute('aria-controls', 'menu-drawer');
        this.links.categories.setAttribute('aria-expanded', String(this.menuDetails.open));
        this.menuDetails.addEventListener('toggle', () => {
          this.links.categories.setAttribute('aria-expanded', String(this.menuDetails.open));
          if (!this.menuDetails.open && this.menuOpenedFromNav) {
            this.menuOpenedFromNav = false;
            this.links.categories.focus({ preventScroll: true });
          }
        }, options);
      }
      if (typeof document.querySelector('cart-drawer')?.open === 'function') {
        this.links.cart.setAttribute('role', 'button');
        this.links.cart.setAttribute('aria-haspopup', 'dialog');
        this.links.cart.setAttribute('aria-controls', 'CartDrawer');
        this.links.cart.setAttribute('aria-expanded', 'false');
      }
      const search = document.getElementById('Inline-Search');
      if (search || document.querySelector('details-modal.header__search')) this.links.search.setAttribute('role', 'button');
      if (search) {
        search.addEventListener('keydown', event => {
          if (event.key !== 'Escape') return;
          search.closest('predictive-search')?.close?.();
          search.blur();
          this.links.search.focus({ preventScroll: true });
        }, options);
      }
      this.badgeObserver?.disconnect();
      const owner = document.getElementById('cart-icon-bubble');
      if (owner) {
        this.badgeObserver = new MutationObserver(() => {
          this.revision += 1;
          this.setCount(this.readCount(owner));
        });
        this.badgeObserver.observe(owner, { childList: true, subtree: true, characterData: true });
        this.setCount(this.readCount(owner));
      }
    }

    revealHeader() {
      document.documentElement.classList.add('wm-mobile-nav-reveal-header');
      document.querySelector('sticky-header')?.reveal?.();
      if (this.headerFrame) cancelAnimationFrame(this.headerFrame);
      this.headerFrame = requestAnimationFrame(() => {
        document.documentElement.classList.remove('wm-mobile-nav-reveal-header');
      });
    }

    activate(event) {
      const link = event.target.closest('[data-wm-nav-action]');
      if (!link || !this.contains(link) || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const action = link.dataset.wmNavAction;
      if (action === 'categories') {
        const details = document.getElementById('Details-menu-drawer-container');
        const summary = details?.querySelector(':scope > summary');
        if (!summary || typeof details.closest('header-drawer')?.openMenuDrawer !== 'function') return;
        event.preventDefault();
        this.revealHeader();
        this.menuOpenedFromNav = true;
        if (!details.open) summary.click();
        summary.focus({ preventScroll: true });
      } else if (action === 'cart') {
        const drawer = document.querySelector('cart-drawer');
        if (typeof drawer?.open !== 'function') return;
        event.preventDefault();
        drawer.open(link);
      } else if (action === 'search') {
        const input = document.getElementById('Inline-Search');
        if (input && input.getClientRects().length) {
          event.preventDefault();
          this.revealHeader();
          input.focus({ preventScroll: true });
          const rect = input.getBoundingClientRect();
          if (rect.top < 0 || rect.bottom > window.innerHeight) input.scrollIntoView({ block: 'center', behavior: 'instant' });
        } else {
          const modal = document.querySelector('details-modal.header__search');
          const summary = modal?.querySelector('summary');
          if (!summary || typeof modal.open !== 'function') return;
          event.preventDefault();
          this.revealHeader();
          summary.click();
        }
      }
    }

    readCount(root) {
      if (!root) return undefined;
      const bubble = root.querySelector('.cart-count-bubble');
      if (!bubble) return 0;
      const value = bubble.querySelector('[aria-hidden="true"]')?.textContent.trim();
      return /^\d+$/.test(value || '') ? Number(value) : undefined;
    }

    setCount(count) {
      if (!Number.isSafeInteger(count) || count < 0) return;
      this.dataset.cartCount = String(count);
      this.badge.textContent = count > 99 ? '99+' : String(count);
      this.badge.hidden = count === 0;
      this.links.cart.setAttribute('aria-label', this.dataset.countLabel.replace('__COUNT__', String(count)));
    }

    cartChanged(detail) {
      this.revision += 1;
      window.clearTimeout(this.refreshTimer);
      const data = detail?.cartData;
      if (Number.isSafeInteger(data?.item_count)) return this.setCount(data.item_count);
      const html = data?.sections?.['cart-icon-bubble'];
      if (typeof html === 'string' && html.includes('shopify-section-cart-icon-bubble')) {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const count = this.readCount(parsed.getElementById('shopify-section-cart-icon-bubble'));
        if (Number.isSafeInteger(count)) return this.setCount(count);
      }
      this.queueRefresh();
    }

    queueRefresh() {
      window.clearTimeout(this.refreshTimer);
      this.refreshTimer = window.setTimeout(() => this.refreshCount(), 120);
    }

    async refreshCount() {
      this.refreshRequest?.abort();
      this.refreshRequest = new AbortController();
      const revision = this.revision;
      try {
        const response = await fetch(`${this.dataset.cartUrl}.js`, {
          signal: this.refreshRequest.signal,
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) return;
        const cart = await response.json();
        if (this.isConnected && this.mobile && revision === this.revision) this.setCount(cart.item_count);
      } catch (error) {
        if (error.name !== 'AbortError') this.setCount(this.readCount(document.getElementById('cart-icon-bubble')));
      }
    }
  }

  customElements.define('wm-mobile-app-nav', WmMobileAppNav);
}

(() => {
  if (!document.body.classList.contains('index-template')) return;
  let discovery;
  const connect = () => {
    const host = document.getElementById('app-embed-container-191292');
    const root = host?.shadowRoot;
    if (!root) return;
    discovery?.disconnect();
    if (host.hasAttribute('data-wm-forms-ready')) return;
    host.setAttribute('data-wm-forms-ready', '');
    let restoreFocus = false;
    let frame = 0;
    const styles = document.createElement('style');
    styles.setAttribute('data-wm-form-styles', '');
    styles.textContent = `
      :host(:dir(rtl)) #first_name { text-align: right; }
      :host(:dir(rtl)) :is(#label-first_name, #label-email) {
        left: auto;
        right: var(--form-field-padding, 15px);
        text-align: right;
        transform-origin: right center;
      }
      #email { direction: ltr; text-align: left; }
      [role="dialog"][aria-label] { --forms-padding-top: 64px; }
      [role="dialog"][aria-label] > div:has(> [aria-label="Close modal"]) {
        top: 8px;
        right: 8px;
        left: auto;
      }
      [role="dialog"] [aria-label="Close modal"] {
        min-width: 44px;
        min-height: 44px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      :host(:dir(rtl)) [role="dialog"][aria-label] > div:has(> [aria-label="Close modal"]) {
        left: 8px;
        right: auto;
      }
      @media screen and (max-width: 749.98px) {
        form input { font-size: 16px !important; }
        :is(#label-DD, #label-MM, #label-YYYY) {
          left: 8px;
          right: auto;
          white-space: nowrap;
        }
        form input[pattern="[0-9]"] {
          padding-left: 8px;
          padding-right: 8px;
          direction: ltr;
          text-align: left;
        }
        [role="dialog"][aria-label] {
          --forms-padding-left: 20px;
          --forms-padding-right: 20px;
          box-sizing: border-box;
          min-width: 0 !important;
          width: calc(100% - 24px) !important;
          max-width: 397px !important;
        }
      }
    `;
    const sync = () => {
      frame = 0;
      if (!styles.isConnected) root.prepend(styles);
      const dialog = root.querySelector('[role="dialog"][aria-label]');
      host.toggleAttribute('data-wm-dialog-open', Boolean(dialog));
      host.toggleAttribute('data-wm-teaser-visible', Boolean(root.querySelector('[data-testid="form-teaser"]')));
      if (restoreFocus && !dialog) {
        const teaser = root.querySelector('[data-testid="form-teaser"]');
        if (teaser) {
          restoreFocus = false;
          teaser.focus({ preventScroll: true });
        }
      }
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(sync);
    };
    root.addEventListener('click', event => {
      const dialog = root.querySelector('[role="dialog"][aria-label]');
      const close = dialog?.querySelector('[aria-label="Close modal"]');
      if (close && event.composedPath().includes(close)) restoreFocus = true;
    }, { capture: true });
    root.addEventListener('keydown', event => {
      if (event.defaultPrevented) return;
      const dialog = root.querySelector('[role="dialog"][aria-label]');
      if (!dialog) return;
      if (event.key === 'Tab') {
        const stops = [];
        const collect = node => {
          for (const element of node.children) {
            if (element.matches('button,[href],input,select,textarea,[tabindex],iframe') && element.tabIndex >= 0 && !element.matches(':disabled') && element.getClientRects().length && getComputedStyle(element).visibility === 'visible') stops.push(element);
            collect(element.shadowRoot || element);
          }
        };
        collect(dialog);
        let active = root.activeElement;
        while (active?.shadowRoot?.activeElement) active = active.shadowRoot.activeElement;
        const first = stops[0];
        const last = stops[stops.length - 1];
        if (event.shiftKey && (active === first || active === dialog)) {
          event.preventDefault();
          last?.focus({ preventScroll: false });
        } else if (!event.shiftKey && active === last) {
          event.preventDefault();
          first?.focus({ preventScroll: false });
        }
        return;
      }
      if (event.key !== 'Escape') return;
      const close = dialog?.querySelector('[aria-label="Close modal"]');
      if (!close) return;
      event.preventDefault();
      event.stopPropagation();
      restoreFocus = true;
      close.click();
      schedule();
    });
    const observer = new MutationObserver(schedule);
    observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-current-step'] });
    sync();
  };
  customElements.whenDefined('shopify-forms-embed').then(() => {
    connect();
    if (!document.getElementById('app-embed-container-191292')?.hasAttribute('data-wm-forms-ready')) {
      discovery = new MutationObserver(connect);
      discovery.observe(document.body, { childList: true, subtree: true });
    }
  });
})();
